import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useRecaptchaVerification } from '../hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { verifyRecaptcha, verifying, error: recaptchaError } = useRecaptchaVerification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || verifying) return; // Prevent multiple submissions
    
    setError('');
    setLoading(true);
    
    try {
      // Verify reCAPTCHA first
      const isVerified = await verifyRecaptcha();
      
      if (!isVerified) {
        setError(recaptchaError || 'reCAPTCHA verification failed. Please try again.');
        setLoading(false);
        return;
      }
      
      // Proceed with sign in
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('auth.login.unexpectedError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 pb-8 md:py-12">
      <div className="panel w-full max-w-md">
        <h1 className="font-tilt-warp text-3xl text-light text-center mb-8">{t('auth.login.title')}</h1>
        
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-light/80 mb-2">
              {t('auth.login.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none transition-colors"
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-light/80 mb-2">
              {t('auth.login.passwordLabel')}
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none transition-colors"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn-secondary w-full h-12 flex items-center justify-center"
            disabled={loading || verifying}
          >
            {loading || verifying ? (
              <span className="flex items-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('auth.login.signingIn')}</span>
              </span>
            ) : (
              t('auth.login.signInButton')
            )}
          </button>
        </form>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-custom/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#1a1a1a] text-light/40">{t('auth.login.orDivider')}</span>
          </div>
        </div>

        <GoogleSignInButton mode="signin" />

        <p className="mt-8 text-center text-light/60">
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="text-secondary hover:text-secondary/80 transition-colors">
            {t('auth.login.signUp')}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;